import { FC, useCallback, useEffect, useRef } from "react";
import { Polyline } from "@evergis/sgis/es/features/Polyline";
import { DrawingFinishEvent } from "@evergis/sgis/es/controls/Control";
import { useMapContext } from "@evergis/react";

import { ShadowedPolySymbol } from "../../../../../react-sgis/symbols/editorSymbols/ShadowedPolySymbol";
import { BasePolylineCreator } from "../../../../../react-sgis/controls/baseControls/BasePolylineCreator";

import { useGeometrySelectionContext } from "../../../../../hooks/geometrySelection/useGeometrySelectionContext";
import { useSelectionEdit } from "./hooks/useSelectionEdit";
import { useGeometrySelection } from "../../../../../hooks/geometrySelection/useGeometrySelection";
import { createPolylineFeature } from "./utils/createPolylineFeature";
import { GeometrySelectionSymbolType, useSelectionSymbols } from "./hooks/useSelectionSymbols";

import { GeometrySelectionToolTypes, GeometrySelectionTypes } from "../types";
import { SelectionProps } from "./types";

export const LineSelection: FC<SelectionProps> = ({ mode }) => {
  const { addFeature, replaceFeature } = useGeometrySelectionContext();
  const [{ history }] = useGeometrySelection();
  const { map, painter } = useMapContext();
  const createControl = useRef<BasePolylineCreator>();
  const symbols = useSelectionSymbols();

  const switchMode = useCallback((create: boolean) => {
    createControl.current.isActive = create;
  }, []);

  const createFeature = useCallback(
    (event: DrawingFinishEvent<Polyline>) => {
      const feature = createPolylineFeature(
        [...event.feature.rings],
        map.crs,
        map.resolution,
        symbols[GeometrySelectionSymbolType.polygonEdit] as ShadowedPolySymbol,
        symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
        symbols[GeometrySelectionSymbolType.polylineEdit] as ShadowedPolySymbol,
        symbols[GeometrySelectionSymbolType.polylineCreate] as ShadowedPolySymbol,
      );

      if (mode === GeometrySelectionToolTypes.Replace && history.step > -1) {
        replaceFeature(feature);
      } else {
        addFeature(feature);
      }
    },
    [map.crs, map.resolution, symbols, mode, history.step, replaceFeature, addFeature],
  );

  const offEvents = useCallback(() => {
    createControl.current.off("drawingFinish", createFeature);
  }, [createFeature]);

  const clearProperties = useCallback(() => {
    createControl.current = undefined;
  }, []);

  const initCreateMode = useCallback(() => {
    if (!createControl.current) {
      createControl.current = new BasePolylineCreator(map, painter, {
        isActive: false,
        useTempLayer: true,
        symbol: symbols[GeometrySelectionSymbolType.polylineCreate].clone() as ShadowedPolySymbol,
        dblClickMinTime: 1000,
        noLabel: true,
      });

      createControl.current.on("drawingFinish", createFeature);
    }
  }, [createFeature, map, painter]);

  useEffect(() => {
    initCreateMode();
    switchMode(true);

    return () => {
      switchMode(false);
      offEvents();
      clearProperties();
    };
  }, [initCreateMode]); // eslint-disable-line

  useSelectionEdit(GeometrySelectionTypes.Line, map, painter, mode, switchMode);

  return null;
};
